import useProjectId from '../../customHooks/useProjectId'
import { useQuery } from '@tanstack/react-query'
import { useAccessToken } from '../useAccessToken'
import { SheetData } from '../../../../interfaces/grids/SheetData'
import { useCallback } from 'react'
import { apiRequestParams } from '../fetchOptions'
import { useHandleRetry } from '../useHandleRetry'
import { makeResultFetch } from '../genericFetch'
import { removeComments } from '../helpers/removeComments'

export const usePeriodReportSnapshotData = (
  previousSnapshotId: string | null,
  currentSnapshotId: string | null
) => {
  const getAccessToken = useAccessToken()
  const projectId = useProjectId()
  const handleRetry = useHandleRetry()

  // Common query function for snapshots
  const snapshotQueryFunction = useCallback(
    async (snapshotId: string) => {
      return getAccessToken().then((accessToken) => {
        if (!accessToken) throw new Error('Access token is undefined')

        const fetchedData = makeResultFetch<SheetData>(
          `/snapshots/${projectId}/load?guid=${snapshotId}`,
          apiRequestParams(accessToken, 'GET')
        ).then((response) => {
          const { data, message, success } = response

          if (!success) throw Error(message)

          return removeComments(data)
        })

        return fetchedData
      })
    },
    [getAccessToken, projectId]
  )

  // Previous snapshot query
  const previousSnapshot = useQuery({
    queryKey: ['periodReportSnapshot', projectId, previousSnapshotId],
    queryFn: () => snapshotQueryFunction(previousSnapshotId!),
    enabled: !!projectId && !!previousSnapshotId,
    retry: handleRetry,
    staleTime: 5 * 60 * 1000, // 5 minutes - snapshots don't change
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // Current snapshot query
  const currentSnapshot = useQuery({
    queryKey: ['periodReportSnapshot', projectId, currentSnapshotId],
    queryFn: () => snapshotQueryFunction(currentSnapshotId!),
    enabled: !!projectId && !!currentSnapshotId,
    retry: handleRetry,
    staleTime: 5 * 60 * 1000, // 5 minutes - snapshots don't change
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  return {
    previousSnapshot: {
      isLoading: previousSnapshot.isLoading,
      isPending: previousSnapshot.isPending,
      isFetching: previousSnapshot.isFetching,
      error: previousSnapshot.error,
      data: previousSnapshot.data,
    },
    currentSnapshot: {
      isLoading: currentSnapshot.isLoading,
      isPending: currentSnapshot.isPending,
      isFetching: currentSnapshot.isFetching,
      error: currentSnapshot.error,
      data: currentSnapshot.data,
    },
  }
}
